import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { 
  User, LogOut, Trophy, Home, Calendar, Medal, Phone, 
  BookOpen, Menu, Users, Award, Shield, GraduationCap, Plane, Search, 
  MessageCircle, ChevronDown, X, Sparkles, Star, Sun, Moon, RefreshCw, ShoppingBag, HelpCircle
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import NotificationBell from './NotificationBell';
import { playMessageChime, vibrate } from '../utils/chime';

const Navbar = () => {
  const { user, logout, theme, toggleTheme, lang, toggleLang } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tournamentsOpen, setTournamentsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = React.useRef(null);

  // Track scroll for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Poll unread messages count every 30 seconds
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return undefined;
    }
    const API = process.env.REACT_APP_BACKEND_URL;
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        const axios = (await import('axios')).default;
        axios.post(`${API}/api/presence/ping`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
        const res = await axios.get(`${API}/api/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(res.data?.unread_count || 0);
        const count = res.data?.unread_count || 0;
        if (prevUnreadRef.current !== null && count > prevUnreadRef.current) {
          playMessageChime();
          vibrate();
        }
        prevUnreadRef.current = count;
      } catch {
        // silent fail
      }
    };
    fetchUnread();
    const intervalId = setInterval(fetchUnread, 30000);
    // Refetch when navigating away from /messages (likely read messages)
    return () => clearInterval(intervalId);
  }, [user, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setOpen(false);
  };

  const isActive = (path) => location.pathname === path;
  const isTournamentActive = () => 
    location.pathname.includes('/tournaments') || 
    location.pathname.includes('/tournament/');

  // קבוצות ניווט מאורגנות - עם צבעים ייחודיים לכל פריט בהתאם לעיצוב החדש
  const mainNavItems = [
    { path: '/', icon: Home, label: 'דף הבית', emoji: '🏠', gradient: 'from-blue-500 to-cyan-500' },
    { path: '/rankings', icon: Medal, label: 'דירוג שחקנים', emoji: '🏆', gradient: 'from-amber-500 to-yellow-500' },
    { path: '/messages', icon: MessageCircle, label: 'הודעות', emoji: '✉️', gradient: 'from-pink-500 to-rose-500' },
  ];

  const tournamentItems = [
    { path: '/tournaments/daily', icon: Calendar, label: 'תחרויות יומיות', emoji: '📅', gradient: 'from-blue-500 to-cyan-500' },
    { path: '/tournaments/knockout', icon: Trophy, label: 'תחרויות נוקאאוט', emoji: '🏅', gradient: 'from-amber-500 to-orange-500' },
  ];

  const infoItems = [
    { path: '/levels', icon: BookOpen, label: 'הסבר על הרמות', emoji: '📖', gradient: 'from-purple-500 to-indigo-500' },
    { path: '/points-system', icon: Award, label: 'שיטת הניקוד', emoji: '⭐', gradient: 'from-yellow-500 to-amber-500' },
  ];

  const communityItems = [
    { path: '/partner-board', icon: Search, label: 'חיפוש פרטנרים למשחק', emoji: '🎾', gradient: 'from-teal-500 to-emerald-500' },
    { path: '/gear-board', icon: ShoppingBag, label: 'לוח ציוד יד שנייה/חדש', emoji: '🛒', gradient: 'from-orange-500 to-amber-500' },
    { path: '/faq', icon: HelpCircle, label: 'שאלות נפוצות', emoji: '❓', gradient: 'from-blue-500 to-indigo-500' },
    { path: '/coaches', icon: GraduationCap, label: 'חיפוש מאמנים', emoji: '🎓', gradient: 'from-emerald-500 to-teal-500' },
    { path: '/training-camps', icon: Plane, label: 'מחנות אימונים לחו״ל', emoji: '✈️', gradient: 'from-orange-500 to-amber-500' },
    { path: '/whatsapp-community', icon: MessageCircle, label: 'לקהילה בווצאפ', emoji: '💬', gradient: 'from-green-500 to-emerald-500' },
    { path: '/contact', icon: Phone, label: 'יצירת קשר', emoji: '📞', gradient: 'from-cyan-500 to-blue-500' },
  ];

  return (
    <nav
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      className={`
      sticky top-0 z-50 transition-all duration-300
      ${scrolled 
        ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-200' 
        : 'bg-gradient-to-r from-white via-blue-50/50 to-green-50/50 border-b border-transparent'
      }
    `}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-green-500 p-0.5 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <div className="w-full h-full rounded-[14px] bg-white flex items-center justify-center overflow-hidden">
                  <img 
                    src="/assets/tennis-pro-logo.jpg" 
                    alt="Tennis Pro Israel" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center shadow-md">
                <span className="text-[8px]">🎾</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <div className="flex items-center gap-1" dir="ltr">
                <span className="text-2xl font-black bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  Tennis
                </span>
                <span className="text-2xl font-black bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">
                  Pro
                </span>
              </div>
              <div className="flex items-center gap-1 -mt-1 justify-end">
                <span className="text-xs font-medium text-gray-500">Israel</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-full font-bold">
                  🇮🇱
                </span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Main Items */}
            {mainNavItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                className={`
                  relative flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                  ${isActive(item.path) 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md shadow-blue-200' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
              >
                <span className="text-lg">{item.emoji}</span>
                <span>{item.label}</span>
                {item.path === '/messages' && unreadCount > 0 && (
                  <span
                    data-testid="nav-unread-badge"
                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-md ring-2 ring-white animate-pulse"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            ))}

            {/* Tournaments Dropdown */}
            <div className="relative group">
              <button 
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200
                  ${isTournamentActive() 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-orange-200' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
              >
                <span className="text-lg">🏆</span>
                <span>תחרויות</span>
                <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 transform translate-y-2 group-hover:translate-y-0">
                <div className="px-4 pb-2 mb-2 border-b border-gray-100">
                  <p className="text-xs font-bold text-gray-400 uppercase">בחר סוג תחרות</p>
                </div>
                {tournamentItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center gap-3 px-4 py-3 mx-2 rounded-xl hover:bg-gradient-to-r transition-all duration-200
                      ${isActive(item.path) 
                        ? `bg-gradient-to-r ${item.color === 'blue' ? 'from-blue-500 to-cyan-500' : 'from-amber-500 to-orange-500'} text-white` 
                        : `hover:${item.color === 'blue' ? 'from-blue-50 to-cyan-50' : 'from-amber-50 to-orange-50'}`
                      }
                    `}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl
                      ${isActive(item.path) 
                        ? 'bg-white/20' 
                        : item.color === 'blue' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                      {item.emoji}
                    </div>
                    <div>
                      <p className={`font-bold ${isActive(item.path) ? 'text-white' : 'text-gray-800'}`}>
                        {item.label}
                      </p>
                      <p className={`text-xs ${isActive(item.path) ? 'text-white/80' : 'text-gray-500'}`}>
                        {item.color === 'blue' ? 'תחרויות קבוצות' : 'תחרויות הדחה'}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Info Items */}
            {infoItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-xl font-medium transition-all duration-200
                  ${isActive(item.path) 
                    ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white shadow-md shadow-blue-200' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
              >
                <span>{item.emoji}</span>
                <span className="hidden xl:inline">{item.label}</span>
              </Link>
            ))}

            {/* Divider */}
            <div className="w-px h-8 bg-gradient-to-b from-transparent via-gray-300 to-transparent mx-2" />

            {/* Community Items */}
            {communityItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-1.5 px-3 py-2 rounded-xl font-medium transition-all duration-200
                  ${isActive(item.path) 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md shadow-green-200' 
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }
                `}
              >
                <span>{item.emoji}</span>
                <span className="hidden xl:inline text-sm">{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Theme toggle + Language + Notifications (all screens) */}
          <div className="flex items-center gap-1">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle-btn"
              aria-label="Switch language"
              title={lang === 'en' ? 'עברית' : 'English'}
              className="px-2 py-1.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors text-sm font-bold min-w-[38px]"
            >
              {lang === 'en' ? 'עב' : 'EN'}
            </button>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              aria-label="החלף ערכת נושא"
              className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={() => window.location.reload()}
              data-testid="refresh-page-btn"
              aria-label="רענן את האתר"
              title="רענן את האתר"
              className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 hover:rotate-180 transition-all duration-500"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            {user && <NotificationBell />}
          </div>

          {/* Desktop Auth & Admin */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {/* Admin Controls */}
                {['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email) && (
                  <div className="flex items-center gap-2">
                    <Link to="/create-tournament">
                      <Button size="sm" className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-md shadow-green-200 rounded-xl h-9 px-4">
                        <span className="text-lg mr-1">+</span> תחרות
                      </Button>
                    </Link>
                    <Link to="/admin/control" data-testid="navbar-admin-link">
                      <Button size="sm" className="bg-gradient-to-r from-slate-800 to-blue-900 hover:from-slate-900 hover:to-blue-950 text-white shadow-md shadow-slate-300 rounded-xl h-9 px-4">
                        <Shield className="w-4 h-4 mr-1" />
                        ניהול האתר
                      </Button>
                    </Link>
                  </div>
                )}

                {user.is_admin && !user.email?.includes('shafirroy') && (
                  <Link to="/admin">
                    <Button size="sm" className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white shadow-md rounded-xl h-9">
                      <Shield className="w-4 h-4 mr-1" />
                      אדמין
                    </Button>
                  </Link>
                )}

                {/* User Profile */}
                <div className="flex items-center gap-2 pr-2 border-r border-gray-200">
                  <Link to={`/profile/${user.id}`} className="group">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 hover:from-blue-50 hover:to-green-50 transition-all duration-300 border border-gray-200">
                      <Avatar className="w-8 h-8 ring-2 ring-white shadow-md">
                        {user.avatar && <AvatarImage src={user.avatar} alt={user.full_name} />}
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-green-500 text-white text-sm font-bold">
                          {user.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden xl:block">
                        <p className="font-bold text-gray-800 text-sm max-w-[100px] truncate group-hover:text-blue-600 transition-colors">
                          {user.full_name}
                        </p>
                        <p className="text-[10px] text-gray-500 -mt-0.5">הפרופיל שלי</p>
                      </div>
                    </div>
                  </Link>
                </div>

                {/* Logout */}
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl h-9 w-9 p-0"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Link to="/auth">
                <Button className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white shadow-lg shadow-blue-200 rounded-xl px-6 font-bold">
                  <User className="w-4 h-4 mr-2" />
                  התחבר / הירשם
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="rounded-xl hover:bg-gray-100" data-testid="mobile-menu-trigger">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>

            <SheetContent side="right" className="w-80 p-0 bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 brand-font" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
              <SheetTitle className="sr-only">תפריט ניווט</SheetTitle>
              {/* Mobile Header - Brand Design */}
              <div className="relative p-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e2b5e 0%, #17224d 60%, #10203f 100%)' }}>
                {/* Decorative brand glows */}
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl" style={{ background: 'rgba(29,111,242,0.35)' }}></div>
                <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full blur-2xl" style={{ background: 'rgba(16,180,126,0.30)' }}></div>
                
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl p-1.5 shadow-xl flex items-center justify-center" style={{ backgroundColor: '#ffffff' }}>
                        <img src="/assets/tennis-pro-logo.jpg" alt="Tennis Pro" className="w-full h-full object-contain rounded-lg" data-testid="mobile-menu-logo" />
                      </div>
                      <div>
                        <h2 className="text-xl font-black text-white tracking-tight">Tennis<span style={{ color: '#1d6ff2' }}>·</span>Pro</h2>
                        <p className="text-sm font-bold bg-gradient-to-r from-[#6ea8ff] to-[#3ddfa9] bg-clip-text text-transparent">ISRAEL 🇮🇱</p>
                      </div>
                    </div>
                  </div>
                  
                  {user && (
                    <Link to={`/profile/${user.id}`} onClick={() => setOpen(false)}>
                      <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 hover:bg-white/20 transition-all">
                        <Avatar className="w-12 h-12 ring-2 ring-[#1d6ff2]/60 shadow-lg">
                          {user.avatar && <AvatarImage src={user.avatar} alt={user.full_name} />}
                          <AvatarFallback className="font-black" style={{ backgroundColor: '#ffffff', color: '#1e2b5e' }}>
                            {user.full_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-black text-white text-base">{user.full_name}</p>
                          <p className="text-white/70 text-xs">צפה בפרופיל ←</p>
                        </div>
                        <ChevronDown className="w-5 h-5 text-white/70 -rotate-90" />
                      </div>
                    </Link>
                  )}
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
                {/* Reusable item renderer */}
                {[
                  { title: 'ראשי', items: mainNavItems },
                  { title: 'תחרויות', items: tournamentItems },
                  { title: 'מידע', items: infoItems },
                  { title: 'קהילה', items: communityItems },
                ].map((section) => (
                  <div key={section.title} className="space-y-1.5 pt-3 first:pt-1">
                    <div className="flex items-center gap-2 px-3 mb-1">
                      <p className="text-[11px] font-black uppercase tracking-wider text-[#1e2b5e] dark:text-cyan-300/80">{section.title}</p>
                      <div className="flex-1 h-px bg-gradient-to-l from-slate-200 dark:from-slate-700 to-transparent"></div>
                    </div>
                    {section.items.map((item) => {
                      const active = isActive(item.path);
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${item.path.replace(/\//g, '-')}`}
                          className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-2xl font-semibold transition-all duration-200 overflow-hidden ${
                            active
                              ? 'text-white shadow-lg'
                              : 'text-[#1e2b5e] dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800/70'
                          }`}
                          style={active ? { background: 'linear-gradient(90deg, #1d6ff2 0%, #1e2b5e 100%)', boxShadow: '0 8px 24px rgba(30,43,94,0.25)' } : {}}
                        >
                          {active && (
                            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/15 rounded-full blur-xl pointer-events-none"></div>
                          )}
                          <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                            active
                              ? 'bg-white/20 backdrop-blur'
                              : 'bg-slate-100 dark:bg-slate-800 group-hover:scale-110 group-hover:bg-[#1d6ff2]/10 dark:group-hover:bg-[#1d6ff2]/25'
                          }`}>
                            <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-[#1d6ff2] dark:text-[#6ea8ff]'}`} />
                          </div>
                          <span className="relative flex-1">{item.label}</span>
                          {item.path === '/messages' && unreadCount > 0 && (
                            <span className="relative min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-md ring-2 ring-white dark:ring-slate-900">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                          {active && item.path !== '/messages' && (
                            <ChevronDown className="relative w-4 h-4 -rotate-90 text-white/80" />
                          )}
                          {active && item.path === '/messages' && unreadCount === 0 && (
                            <ChevronDown className="relative w-4 h-4 -rotate-90 text-white/80" />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                ))}

                {/* Admin Section */}
                {['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email) && (
                  <div className="space-y-1.5 pt-3">
                    <div className="flex items-center gap-2 px-3 mb-1">
                      <span className="text-base text-red-500">🔐</span>
                      <p className="text-[11px] font-black text-red-500 uppercase tracking-wider">אדמין</p>
                      <div className="flex-1 h-px bg-gradient-to-l from-red-200 to-transparent"></div>
                    </div>
                    {[
                      { to: '/create-tournament', icon: null, emoji: '➕', label: 'צור תחרות חדשה', gradient: 'from-emerald-500 to-teal-500' },
                      { to: '/admin/control', icon: Shield, emoji: null, label: 'ניהול האתר', gradient: 'from-slate-800 to-blue-900' },
                      { to: '/admin/stats', icon: null, emoji: '📊', label: 'דשבורד סטטיסטיקות', gradient: 'from-indigo-600 to-purple-600' },
                    ].map((adm) => (
                      <Link
                        key={adm.to}
                        to={adm.to}
                        onClick={() => setOpen(false)}
                        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-2xl font-semibold text-white bg-gradient-to-l ${adm.gradient} shadow-lg overflow-hidden`}
                        style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                      >
                        <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/20 rounded-full blur-xl pointer-events-none"></div>
                        <div className="relative w-10 h-10 rounded-xl bg-white/25 backdrop-blur flex items-center justify-center shrink-0">
                          {adm.icon ? <adm.icon className="w-5 h-5" /> : <span className="text-lg">{adm.emoji}</span>}
                        </div>
                        <span className="relative">{adm.label}</span>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Auth */}
                <div className="pt-6 mt-3 border-t border-slate-200 dark:border-slate-800">
                  {user ? (
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-3 rounded-2xl font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 w-full transition-all"
                    >
                      <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-950/60 flex items-center justify-center">
                        <LogOut className="w-5 h-5" />
                      </div>
                      <span>התנתק</span>
                    </button>
                  ) : (
                    <Link
                      to="/auth"
                      onClick={() => setOpen(false)}
                      className="relative flex items-center justify-center gap-2 px-4 py-4 rounded-2xl font-black bg-gradient-to-l from-blue-600 to-emerald-500 text-white shadow-xl overflow-hidden"
                      style={{ boxShadow: '0 12px 32px rgba(37, 99, 235, 0.3)' }}
                    >
                      <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
                      <User className="relative w-5 h-5" />
                      <span className="relative">התחבר / הירשם</span>
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

